import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUp, ArrowDown, ExternalLink, Copy, Save } from "lucide-react";
import { api } from "../lib/api";

const TYPES = ["short_text", "long_text", "email", "phone", "url", "single_choice"];
const MAPS = ["none", "first_name", "last_name", "email", "phone", "company"];
const uid = () => "q_" + Math.random().toString(36).slice(2, 8);

export default function FormManager() {
  const [config, setConfig] = useState(null);
  const [subs, setSubs] = useState([]);
  const [saving, setSaving] = useState(false);
  const applyUrl = `${window.location.origin}/apply`;

  useEffect(() => {
    api.get("/form").then(({ data }) => setConfig(data));
    api.get("/prospects").then(({ data }) => setSubs(data.filter((p) => p.source === "application")));
  }, []);

  if (!config) return <p className="eyebrow">Loading form…</p>;

  const setField = (k, v) => setConfig((c) => ({ ...c, [k]: v }));
  const setQ = (i, patch) => setConfig((c) => {
    const questions = [...c.questions];
    questions[i] = { ...questions[i], ...patch };
    return { ...c, questions };
  });
  const move = (i, dir) => setConfig((c) => {
    const questions = [...c.questions];
    const j = i + dir;
    if (j < 0 || j >= questions.length) return c;
    [questions[i], questions[j]] = [questions[j], questions[i]];
    return { ...c, questions };
  });
  const remove = (i) => setConfig((c) => ({ ...c, questions: c.questions.filter((_, idx) => idx !== i) }));
  const add = () => setConfig((c) => ({ ...c, questions: [...c.questions, { id: uid(), label: "New question", type: "short_text", required: false, options: [], maps_to: null }] }));

  const save = async () => {
    if (!config.questions.length) return toast.error("Add at least one question");
    const badChoice = config.questions.find((q) => q.type === "single_choice" && (!q.options || q.options.length === 0));
    if (badChoice) return toast.error(`"${badChoice.label}" is a choice question with no options`);
    if (!config.questions.some((q) => q.maps_to === "email")) return toast.error("At least one question must map to email");
    setSaving(true);
    try {
      await api.put("/form", { config });
      toast.success("Form saved");
    } catch {
      toast.error("Could not save form");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#4690d9]";

  return (
    <div className="space-y-6" data-testid="form-manager-page">
      <div className="flex flex-wrap items-center justify-between gap-3 fade-up">
        <div>
          <p className="eyebrow mb-1">Form Management</p>
          <h1 className="text-3xl font-extrabold tracking-tight text-white">Application Form</h1>
        </div>
        <div className="flex items-center gap-2">
          <a href={applyUrl} target="_blank" rel="noreferrer" data-testid="view-form-link" className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-white/15 text-slate-300 hover:text-white text-sm font-semibold transition-colors">
            <ExternalLink size={14} /> View form
          </a>
          <button data-testid="copy-form-link" onClick={() => { navigator.clipboard.writeText(applyUrl); toast.success("Form link copied"); }} className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-white/15 text-slate-300 hover:text-white text-sm font-semibold transition-colors">
            <Copy size={14} /> Copy link
          </button>
          <button data-testid="save-form-btn" onClick={save} disabled={saving} className="flex items-center gap-1.5 px-5 py-2 rounded-full bg-[#4690d9] hover:bg-[#5a9fe0] text-white text-sm font-semibold transition-colors disabled:opacity-50">
            <Save size={14} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <div className="glass-panel p-5 space-y-3 fade-up">
        <p className="eyebrow text-[10px]">Form header &amp; messaging</p>
        <div>
          <label className="text-xs text-slate-500 block mb-1">Title</label>
          <input data-testid="form-title-input" className={inputCls} value={config.title || ""} onChange={(e) => setField("title", e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">Description</label>
          <input data-testid="form-description-input" className={inputCls} value={config.description || ""} onChange={(e) => setField("description", e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">Success message (shown if no Calendly link is set)</label>
          <input data-testid="form-success-input" className={inputCls} value={config.success_message || ""} onChange={(e) => setField("success_message", e.target.value)} />
        </div>
      </div>

      <div className="space-y-3 fade-up">
        <div className="flex items-center justify-between">
          <p className="eyebrow text-[10px]">Questions ({config.questions.length})</p>
          <button data-testid="add-question-btn" onClick={add} className="flex items-center gap-1.5 text-xs font-semibold text-[#9cc6f0] hover:text-white"><Plus size={14} /> Add question</button>
        </div>
        {config.questions.map((q, i) => (
          <div key={q.id} className="glass-panel p-4" data-testid={`question-row-${i}`}>
            <div className="flex items-start gap-3">
              <div className="flex flex-col gap-1 pt-1">
                <button onClick={() => move(i, -1)} className="text-slate-500 hover:text-white" data-testid={`q-up-${i}`}><ArrowUp size={14} /></button>
                <button onClick={() => move(i, 1)} className="text-slate-500 hover:text-white" data-testid={`q-down-${i}`}><ArrowDown size={14} /></button>
              </div>
              <div className="flex-1 space-y-2">
                <input className={inputCls} value={q.label} onChange={(e) => setQ(i, { label: e.target.value })} data-testid={`q-label-${i}`} placeholder="Question label" />
                <div className="flex flex-wrap items-center gap-3">
                  <select className={`${inputCls} w-auto`} value={q.type} onChange={(e) => setQ(i, { type: e.target.value })} data-testid={`q-type-${i}`}>
                    {TYPES.map((t) => <option key={t} value={t} className="bg-[#0f2442]">{t}</option>)}
                  </select>
                  <select className={`${inputCls} w-auto`} value={q.maps_to || "none"} onChange={(e) => setQ(i, { maps_to: e.target.value === "none" ? null : e.target.value })} data-testid={`q-maps-${i}`}>
                    {MAPS.map((m) => <option key={m} value={m} className="bg-[#0f2442]">maps to: {m}</option>)}
                  </select>
                  <label className="flex items-center gap-1.5 text-xs text-slate-300">
                    <input type="checkbox" checked={!!q.required} onChange={(e) => setQ(i, { required: e.target.checked })} data-testid={`q-required-${i}`} /> Required
                  </label>
                  <button onClick={() => remove(i)} className="ml-auto text-rose-400/70 hover:text-rose-400" data-testid={`q-delete-${i}`}><Trash2 size={15} /></button>
                </div>
                {q.type === "single_choice" && (
                  <textarea
                    className={`${inputCls} resize-none`}
                    rows={3}
                    placeholder="One option per line"
                    value={(q.options || []).join("\n")}
                    onChange={(e) => setQ(i, { options: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
                    data-testid={`q-options-${i}`}
                  />
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="glass-panel p-5 fade-up" data-testid="submissions-panel">
        <p className="eyebrow text-[10px] mb-3">Recent submissions ({subs.length})</p>
        {subs.length === 0 ? (
          <p className="text-sm text-slate-500">No submissions from this form yet.</p>
        ) : (
          <div className="space-y-2">
            {subs.slice(0, 15).map((p) => (
              <div key={p.id} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
                <span className="text-white font-medium">{p.name} <span className="text-slate-500 font-normal">· {p.email}</span></span>
                <span className="text-xs text-slate-500 font-mono-tech">{new Date(p.created_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
